import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useParams } from "react-router"
import ProgressiveTemple, { ProgressiveLevelCard } from '../components/ProgressiveTemple'
import { getCurrencyConfig } from '../components/services'
import { Apis } from "../util/Apis"
import { AnimatePresence } from 'motion/react'
import useGet from '../hooks/useGet'
import useWindowSize from '../hooks/useWindowSize'
import winnerSound from '../assets/sounds/winner.mp3'
import { isWinnerSoundMuted } from '../util/winnerSound'
import ProgressiveLevelModal, { ModalActionButton } from '../components/ProgressiveLevelModal'
import { HiOutlineSlotMachine } from '../components/Icons'
import { SiteContext } from '../context/SiteContext'


const ProgressiveView = () => {
  const { id } = useParams()
  const size = useWindowSize()
  const { currencyConfig: contextCurrencyConfig, salaId } = useContext(SiteContext)
  const currencyConfig = contextCurrencyConfig || getCurrencyConfig()

  const [acknowledgedWinnerKey, setAcknowledgedWinnerKey] = useState(null)
  const [updatingWinnerStatus, setUpdatingWinnerStatus] = useState(false)
  const [previewLevelId, setPreviewLevelId] = useState(null)
  const winnerAudioRef = useRef(null)

  const { data: dataProgressive, fetchData } = useGet({
    url: Apis.TGM + '/progressive/' + id + '/detail'
  })

  const sortedLevels = useMemo(
    () => [...(dataProgressive?.levels || [])].sort((a, b) => (b.baseAmount || 0) - (a.baseAmount || 0)),
    [dataProgressive]
  )
  const currentWinnerLevel = useMemo(
    () => sortedLevels.find(level => level.winner),
    [sortedLevels]
  )
  const currentWinnerKey = currentWinnerLevel
    ? `${currentWinnerLevel.id}-${currentWinnerLevel.winnerDatetime || 'winner'}`
    : null
  const showWinnerModal = Boolean(currentWinnerKey && currentWinnerKey !== acknowledgedWinnerKey)
  const previewLevel = useMemo(
    () => sortedLevels.find(level => level.id === previewLevelId) || null,
    [sortedLevels, previewLevelId]
  )
  const previewLevelIndex = useMemo(
    () => sortedLevels.findIndex(level => level.id === previewLevelId),
    [sortedLevels, previewLevelId]
  )
  const showPreviewModal = Boolean(previewLevel && !showWinnerModal)

  const renderBanner = (level, index) => (
    <div className="progressive-preview-modal-card">
      <ProgressiveLevelCard level={level} index={index} currency={currencyConfig.symbol} expanded />
    </div>
  )

  const renderWinnerDetails = (level) => {
    if (!level || (!level.name && !level.winnerMachineName)) return null

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          textAlign: 'center',
          textTransform: 'uppercase',
          color: '#fff',
        }}
      >
        {level.winnerMachineName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              fontSize: "72px",
              fontWeight: 800,
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.45)',
            }}
          >
            <span>{level.winnerMachineName}</span>
             <HiOutlineSlotMachine size={64} />
          </div>
        )}
      </div>
    )
  }

  const updateWinnerStatus = (status) => {
    const winnerId = currentWinnerLevel?.progressiveLevelWinnerId
    if (!winnerId || updatingWinnerStatus) return

    setUpdatingWinnerStatus(true)

    axios.put(`${Apis.TGM}/progressive-level-winner/${winnerId}/update-status`, {
      status,
    }).finally(() => {
      setAcknowledgedWinnerKey(currentWinnerKey)
      setUpdatingWinnerStatus(false)
      fetchData()
    })
  }

  const openPreviewLevel = (levelId) => {
    if (showWinnerModal) return
    setPreviewLevelId(levelId)
  }

  const closePreviewLevel = () => {
    setPreviewLevelId(null)
  }

  useEffect(() => {
    const interval = setInterval(() => fetchData(), 3000);

    return () => clearInterval(interval);
  }, [fetchData])

  if (!currentWinnerKey && acknowledgedWinnerKey !== null) {
    setAcknowledgedWinnerKey(null)
  }

  useEffect(() => {
    if (!showWinnerModal || !currentWinnerKey || isWinnerSoundMuted(salaId)) return

    if (!winnerAudioRef.current) {
      winnerAudioRef.current = new Audio(winnerSound)
    }

    winnerAudioRef.current.currentTime = 0
    winnerAudioRef.current.play().catch(() => {})
  }, [showWinnerModal, currentWinnerKey, salaId])

  return (
    <main className="progressive-preview-page progressive-live-page">
      <ProgressiveTemple
        levels={sortedLevels}
        currency={currencyConfig.symbol}
        onOpen={openPreviewLevel}
        covered={showWinnerModal || showPreviewModal}
        fillScreen
        title={dataProgressive?.name}
      >
          <AnimatePresence>
            {showWinnerModal && currentWinnerLevel && (
              <ProgressiveLevelModal
                showConfetti
                confettiWidth={size.width}
                confettiHeight={size.height}
                actions={(
                  <>
                    <ModalActionButton
                      style={{ minWidth: '240px' }}
                      variant="outline"
                      disabled={updatingWinnerStatus}
                      onClick={() => updateWinnerStatus('CANCELLED')}
                    >
                      CANCELAR
                    </ModalActionButton>
                    <ModalActionButton
                      disabled={updatingWinnerStatus}
                      onClick={() => updateWinnerStatus('PAID')}
                    >
                      Confirmar
                    </ModalActionButton>
                  </>
                )}
              >
                {renderBanner(currentWinnerLevel, sortedLevels.findIndex(level => level.id === currentWinnerLevel.id))}
                {renderWinnerDetails(currentWinnerLevel)}
              </ProgressiveLevelModal>
            )}

            {!showWinnerModal && showPreviewModal && previewLevel && (
              <ProgressiveLevelModal
                onClose={closePreviewLevel}
                closeOnOverlayClick
                actions={(
                  <ModalActionButton onClick={closePreviewLevel}>
                    Cerrar vista previa
                  </ModalActionButton>
                )}
              >
                {renderBanner(previewLevel, previewLevelIndex)}
              </ProgressiveLevelModal>
            )}
          </AnimatePresence>
      </ProgressiveTemple>
    </main>
  )
}

export default ProgressiveView
